import { useCallback, useContext, useState } from "react";
import { IContentWithProgress } from "../@types";
import { useContentAndProgress } from "../contexts/UserProgressContext";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../contexts/AuthContext";

// Hook for navigating between milestones
export function useMilestoneNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const journeyId = user?.userDetails.journeyId;
  const {
    activeMilestone,
    currentPhaseMilestones,
    milestones,
    phases,
    activePhase,
  } = useContentAndProgress();

  const [isPhaseChangeModalOpen, setIsPhaseChangeModalOpen] = useState(false);

  const closeModal = () => {
    setIsPhaseChangeModalOpen(false);
  };
  // Move to the next phase and the first unfinished milestone
  const jumpToNextPhase = useCallback(
    (shouldNavigate?: boolean) => {
      if (!phases) return;
      const currentPhaseIndex = phases.findIndex(
        (p: IContentWithProgress) => p.id === activePhase?.id
      );
      if (currentPhaseIndex === -1 || currentPhaseIndex + 1 === phases.length)
        return;

      const nextPhase = phases[currentPhaseIndex + 1];

      // Get the first milestone in the next phase
      const firstMilestone = milestones
        ?.filter((ms: IContentWithProgress) =>
          nextPhase.content.childContentIds.includes(
            ms.content.platformContentId
          )
        )
        .filter((ms: IContentWithProgress) => !ms.markedIrrelevant)
        .sort(
          (ms1: IContentWithProgress, ms2: IContentWithProgress) =>
            ms1.content.orderForProgress[journeyId ?? ""] -
            ms2.content.orderForProgress[journeyId ?? ""]
        )[0];

      // Check if switching to a new phase
      if (firstMilestone && !shouldNavigate) {
        setIsPhaseChangeModalOpen(true); // Open modal instead of navigating
        return; // Stop further navigation
      }
      if (firstMilestone && shouldNavigate) {
        closeModal();
        navigate(
          `/phase/${nextPhase.content.platformContentId}/milestone/${firstMilestone.content.platformContentId}`
        );
      }
    },
    [activePhase, phases, milestones, journeyId]
  );

  // Move to the previous phase and the last milestone
  const jumpToPreviousPhase = useCallback(() => {
    if (!phases) return;
    const currentPhaseIndex = phases.findIndex(
      (p: IContentWithProgress) => p.id === activePhase?.id
    );
    if (currentPhaseIndex === -1 || currentPhaseIndex === 0) return;
    const previousPhase = phases[currentPhaseIndex - 1];

    const lastMilestone = milestones
      ?.filter((ms: IContentWithProgress) =>
        previousPhase.content.childContentIds.includes(
          ms.content.platformContentId
        )
      )
      .sort(
        (ms1: IContentWithProgress, ms2: IContentWithProgress) =>
          ms1.content.orderForProgress[journeyId ?? ""] -
          ms2.content.orderForProgress[journeyId ?? ""]
      )
      .slice(-1)[0];

    navigate(
      `/phase/${previousPhase.content.platformContentId}/milestone/${lastMilestone?.content.platformContentId}`
    );
  }, [activePhase, phases, milestones, journeyId, navigate]);

  // Go to the next milestone in the current phase, or move to the next phase if needed
  const goToNextMilestone = useCallback(() => {
    if (!currentPhaseMilestones) return;

    const filteredMilestones = currentPhaseMilestones.filter(
      (milestone: IContentWithProgress) => !milestone.markedIrrelevant
    );

    if (filteredMilestones.length === 0) {
      jumpToNextPhase(false);
      return;
    }

    let currentMilestoneIndex = filteredMilestones.findIndex(
      (ms: IContentWithProgress) => ms.id === activeMilestone?.id
    );

    if (currentMilestoneIndex === -1) {
      currentMilestoneIndex = -1; // So the next milestone will be index 0
    }

    const nextMilestone = filteredMilestones[currentMilestoneIndex + 1];

    if (nextMilestone) {
      navigate(
        `${location.pathname.split("/").slice(0, 3).join("/")}/milestone/${
          nextMilestone.content.platformContentId
        }`
      );
    } else {
      jumpToNextPhase(false); // Try to move to the next phase if there are no more milestones
    }
  }, [
    currentPhaseMilestones,
    activeMilestone,
    location,
    jumpToNextPhase,
    navigate,
  ]);

  // Go to the previous milestone in the current phase, or move to the previous phase if needed
  const goToPreviousMilestone = useCallback(() => {
    if (!currentPhaseMilestones) return;

    const filteredMilestones = currentPhaseMilestones.filter(
      (milestone: IContentWithProgress) => !milestone.markedIrrelevant
    );

    if (filteredMilestones.length === 0) {
      jumpToPreviousPhase();
      return;
    }

    let currentMilestoneIndex = filteredMilestones.findIndex(
      (ms: IContentWithProgress) => ms.id === activeMilestone?.id
    );

    if (currentMilestoneIndex === -1) {
      currentMilestoneIndex = filteredMilestones.length; // So the previous milestone will be the last one
    }

    const previousMilestone = filteredMilestones[currentMilestoneIndex - 1];
    if (previousMilestone) {
      navigate(
        `${location.pathname.split("/").slice(0, 3).join("/")}/milestone/${
          previousMilestone.content.platformContentId
        }`
      );
    } else {
      jumpToPreviousPhase(); // Move to the previous phase if there are no earlier milestones
    }
  }, [
    currentPhaseMilestones,
    activeMilestone,
    location,
    jumpToPreviousPhase,
    navigate,
  ]);

  return {
    jumpToNextPhase,
    goToNextMilestone,
    goToPreviousMilestone,
    isPhaseChangeModalOpen,
    closeModal,
    setIsPhaseChangeModalOpen,
  };
}
